import { GQL } from "@binale-tech/shared";

import { withThinking } from "../runner/runAgent";
import type { AgentStep } from "../runner/types";

const STALE_BANK_DAYS = 30;
const DAY_MS = 86_400_000;

export const startStep: AgentStep = {
    id: "start",
    async run(ctx, io) {
        const [buckets, documents, mailbox] = await withThinking(io, "app.agent.start.thinking", () =>
            Promise.all([
                ctx.readPendingBuckets(),
                ctx.canRead(GQL.IProductKey.Dms) ? ctx.countNewDocuments() : Promise.resolve(null),
                ctx.readMailboxConfig().catch((error: unknown): null => {
                    console.error("Agent start: mailbox read failed", error);
                    return null;
                }),
            ]),
        );
        const connected = ctx.banks().filter((b) => b.finapiBankConnectionId != null);
        if (documents === null) {
            io.post({
                kind: "text",
                textId: "app.agent.start.overviewNoDocuments",
                values: { year: ctx.year, banks: connected.length },
            });
        } else {
            io.post({
                kind: "text",
                textId: "app.agent.start.overview",
                values: { year: ctx.year, documents, banks: connected.length },
            });
        }

        const sum = (kind: GQL.IPendingBucketKind) =>
            buckets.filter((b) => b.kind === kind).reduce((total, b) => total + b.count, 0);
        const open = {
            documents: sum(GQL.IPendingBucketKind.DocumentsReadyToBook),
            bank: sum(GQL.IPendingBucketKind.BankTransactionsNotBooked),
            kasse: sum(GQL.IPendingBucketKind.KasseTransactionsNotBooked),
        };
        if (open.documents + open.bank + open.kasse > 0) {
            io.post({ kind: "text", textId: "app.agent.start.open", values: { year: ctx.year, ...open } });
        }

        if (mailbox?.connectionStatus === GQL.IMailboxConnectionStatus.Error) {
            io.post({ kind: "warning", textId: "app.agent.start.mailboxError" });
        }
        const today = new Date().toISOString().slice(0, 10);
        for (const bank of connected) {
            if (!bank.finapiImportDate) continue;
            const ageDays = (Date.parse(today) - Date.parse(bank.finapiImportDate.slice(0, 10))) / DAY_MS;
            if (ageDays > STALE_BANK_DAYS) {
                io.post({ kind: "warning", textId: "app.agent.start.bankStale", values: { bank: bank.bankName } });
            }
        }

        if (ctx.canWrite(GQL.IProductKey.Dms)) {
            const answer = await io.ask("app.agent.start.uploadQuestion", [
                { value: "upload", labelId: "app.agent.start.upload", primary: true },
                { value: "skip", labelId: "app.agent.start.skipUpload" },
            ]);
            ctx.uploadRequested = answer === "upload";
        }
        return { status: "done" };
    },
};
